"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AreaSelection = exports.MultiSelectionMode = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const styled_components_1 = __importDefault(require("styled-components"));
const ui_1 = require("@completeanatomy/ui");
const bridge_1 = require("@completeanatomy/bridge");
const noop_1 = __importDefault(require("lodash/noop"));
const defaultPosition = {
    x: 0,
    y: 0,
};
var MultiSelectionMode;
(function (MultiSelectionMode) {
    MultiSelectionMode["Select"] = "select";
    MultiSelectionMode["Deselect"] = "deselect";
    MultiSelectionMode["None"] = "none";
})(MultiSelectionMode || (exports.MultiSelectionMode = MultiSelectionMode = {}));
function AreaSelection({ onSelectionEnd = noop_1.default, selectionMode = MultiSelectionMode.None, }) {
    const bridge = (0, bridge_1.useBridge)();
    const selectionOrigin = (0, react_1.useRef)(defaultPosition);
    const [selectionTarget, setSelectionTarget] = (0, react_1.useState)(defaultPosition);
    const onDragEnd = (0, react_1.useCallback)(({ origin, target, height, width }) => {
        const x = Math.min(origin.x, target.x);
        const y = Math.min(origin.y, target.y);
        bridge === null || bridge === void 0 ? void 0 : bridge.selectMultipleParts({
            rect: {
                x: x,
                y: y,
                height: height,
                width: width,
            },
            selectParts: selectionMode === MultiSelectionMode.Select,
        });
        onSelectionEnd();
    }, [bridge, onSelectionEnd, selectionMode]);
    const handleDrag = (0, react_1.useCallback)((_, phase, data) => {
        const { position } = data;
        switch (phase) {
            case ui_1.ActionPhase.Start: {
                selectionOrigin.current = position;
                break;
            }
            case ui_1.ActionPhase.Perform: {
                setSelectionTarget(position);
                break;
            }
            case ui_1.ActionPhase.End: {
                const currentOrigin = selectionOrigin.current;
                selectionOrigin.current = defaultPosition;
                setSelectionTarget(defaultPosition);
                onDragEnd({
                    origin: currentOrigin,
                    target: position,
                    width: Math.abs(position.x - currentOrigin.x),
                    height: Math.abs(position.y - currentOrigin.y),
                });
                break;
            }
        }
    }, [onDragEnd]);
    const [dragHandlers] = (0, ui_1.useDragHandlers)(handleDrag, {
        preventDefaults: true,
        stopPropagation: true,
        captureEndOnClick: true,
    });
    const handleTransform = () => {
        const verticalOverlap = selectionOrigin.current.y > selectionTarget.y;
        const horizontalOverlap = selectionOrigin.current.x > selectionTarget.x;
        if (verticalOverlap && horizontalOverlap)
            return 'scaleY(-1) scaleX(-1)';
        if (verticalOverlap)
            return 'scaleY(-1)';
        if (horizontalOverlap)
            return 'scaleX(-1)';
        return null;
    };
    const width = Math.abs(selectionTarget.x - selectionOrigin.current.x);
    const height = Math.abs(selectionTarget.y - selectionOrigin.current.y);
    const transform = handleTransform();
    if (selectionMode === MultiSelectionMode.None) {
        return null;
    }
    return ((0, jsx_runtime_1.jsx)(DragLayer, Object.assign({}, dragHandlers, { "data-e2e": 'AreaSelection_DragLayer', children: (0, jsx_runtime_1.jsx)(RectangleSelection, { role: "presentation", left: selectionOrigin.current.x, top: selectionOrigin.current.y, width: width, height: height, transform: transform, "data-e2e": 'AreaSelection_RectangleSelection' }) })));
}
exports.AreaSelection = AreaSelection;
const DragLayer = styled_components_1.default.div `
  position: fixed;
  top: 0;
  left: 0;

  width: 100vw;
  height: 100vh;

  cursor: crosshair;
`;
const RectangleSelection = styled_components_1.default.div.attrs(({ top, left, height, width, transform }) => ({
    style: {
        top,
        left,
        height,
        width,
        transform,
    },
})) `
  position: absolute;

  border: ${({ theme, width, height }) => `${width || height ? 1 : 0}px solid ${theme.heatheredGrey}`};

  transform-box: fill-box;
  transform-origin: top left;

  user-select: none;

  will-change: top, left, height, width, transform;
`;
